import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchSubscribers } from "../api";
import RequireAdmin from "./RequireAdmin";

const formatDate = (value) => {
  if (!value) return "—";
  // Stored as naive UTC isoformat; treat it as UTC so local rendering is correct.
  const iso = value.endsWith("Z") || value.includes("+") ? value : `${value}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

function SubscriberTable({ users }) {
  const [expanded, setExpanded] = useState({});

  if (!users || users.length === 0) {
    return <p className="text-muted mb-0">No one has signed up yet.</p>;
  }

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Signed up</th>
            <th scope="col">Telegram</th>
            <th scope="col" className="text-end">Following</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <React.Fragment key={user.id}>
              <tr>
                <td style={{ wordBreak: "break-all" }}>{user.email}</td>
                <td className="small text-muted">{formatDate(user.created_at)}</td>
                <td>
                  {user.telegram_linked ? (
                    <span className="badge bg-success">linked</span>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
                <td className="text-end">{user.subscription_count}</td>
                <td className="text-end">
                  {user.subscription_count > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggle(user.id)}
                      aria-expanded={Boolean(expanded[user.id])}
                    >
                      {expanded[user.id] ? "Hide" : "Show"}
                    </button>
                  )}
                </td>
              </tr>
              {expanded[user.id] && (
                <tr>
                  <td colSpan={5} className="bg-light">
                    <ul className="mb-0 small">
                      {user.subscriptions.map((sub) => (
                        <li key={`${sub.venue}-${sub.performer_name}`}>
                          <strong>{sub.performer_name}</strong>{" "}
                          <span className="text-muted">({sub.venue_label})</span>{" "}
                          <span className="text-muted">
                            added {formatDate(sub.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Admin report: who has signed up, what they follow, and whether digests are going out.
function AdminSubscribers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchSubscribers());
    } catch (e) {
      setError(e.message || "Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.title = "Subscribers | Sydney Roster Scheduler";
  }, []);

  return (
    <RequireAdmin title="Subscribers">
      {(me) => (
        <div className="container mt-5" style={{ maxWidth: 900 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="h4 mb-0">Subscribers</h1>
            <div className="d-flex gap-2 align-items-center">
              <span className="badge bg-success">admin: {me?.email}</span>
              <Link to="/start" className="btn btn-sm btn-outline-secondary">
                ← Admin
              </Link>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-muted">Loading subscribers...</p>
          ) : data ? (
            <>
              <div className="row g-2 mb-3">
                {[
                  { label: "Users", value: data.user_count },
                  { label: "Subscriptions", value: data.subscription_count },
                  { label: "Digests sent", value: data.notified_total },
                ].map((stat) => (
                  <div className="col-4" key={stat.label}>
                    <div className="card text-center shadow-sm">
                      <div className="card-body py-2">
                        <div className="h4 mb-0">{stat.value}</div>
                        <div className="small text-muted">{stat.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-muted small">
                Last notification sent: {formatDate(data.last_notified_at)}
              </p>

              {data.top_followed && data.top_followed.length > 0 && (
                <div className="card shadow-sm mb-3">
                  <div className="card-body">
                    <h2 className="h6 text-uppercase text-muted small mb-2">
                      Most followed
                    </h2>
                    <ul className="list-inline mb-0">
                      {data.top_followed.map((item) => (
                        <li
                          className="list-inline-item me-2 mb-1"
                          key={`${item.venue}-${item.performer_name}`}
                        >
                          <span className="badge bg-secondary">
                            {item.performer_name} ({item.venue_label}) &times;
                            {item.followers}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h2 className="h6 text-uppercase text-muted small mb-0">
                      All users
                    </h2>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={load}
                    >
                      Refresh
                    </button>
                  </div>
                  <SubscriberTable users={data.users} />
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}
    </RequireAdmin>
  );
}

export default AdminSubscribers;
